import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, HttpError } from "@shared/http/client";
import type { SessionUser, LoginResponse } from "@/types/session";

export const authKeys = {
  me: ["auth", "me"] as const,
};

export function useMe() {
  return useQuery({
    queryKey: authKeys.me,
    queryFn: () => api.get<SessionUser>("/auth/me"),
    retry: (failCount, error) => {
      if (error instanceof HttpError && error.status === 401) return false;
      return failCount < 1;
    },
    staleTime: 5 * 60_000,
  });
}

export const SESSION_LOGIN_TIME_KEY = "foca-session-login-time";

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (creds: { username: string; password: string }) =>
      api.post<LoginResponse>("/auth/login", creds),
    onSuccess: (data) => {
      qc.setQueryData(authKeys.me, data.user);
      localStorage.setItem(SESSION_LOGIN_TIME_KEY, String(Date.now()));
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>("/auth/logout"),
    onSuccess: () => {
      qc.setQueryData(authKeys.me, null);
      qc.clear();
      localStorage.removeItem(SESSION_LOGIN_TIME_KEY);
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      api.post<{ ok: true }>("/auth/change-password", body),
  });
}
